import React, { useState } from 'react';
import './RawJSONView.css';
import { Code, Copy, Check, FileJson } from 'lucide-react';

function RawJSONView({ metadata, searchTerm }) {
  const [copied, setCopied] = useState(false);

  // Filter out AI detection fields - only show actual file metadata
  const AI_DETECTION_FIELDS = [
    'isAIGenerated', 'aiGenerator', 'aiPrompt', 'confidence',
    'evidence', 'deepScanResults', 'c2paVerified', 'iptcVerified',
    'aiType', 'enhancementTool'
  ];

  const filterMetadata = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj;
    const filtered = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!AI_DETECTION_FIELDS.includes(key)) {
        filtered[key] = typeof value === 'object' && value !== null && !Array.isArray(value)
          ? filterMetadata(value)
          : value;
      }
    }
    return filtered;
  };

  const pureMetadata = filterMetadata(metadata);

  const handleCopy = () => {
    if (pureMetadata) {
      navigator.clipboard.writeText(JSON.stringify(pureMetadata, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Syntax highlight JSON
  const syntaxHighlight = (json) => {
    if (typeof json !== 'string') {
      json = JSON.stringify(json, null, 2);
    }

    // Escape HTML
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Add syntax highlighting
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        let cls = 'json-number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'json-key';
            match = match.slice(0, -1) + '<span class="json-colon">:</span>';
          } else {
            cls = 'json-string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean';
        } else if (/null/.test(match)) {
          cls = 'json-null';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  };

  if (!metadata) {
    return (
      <div className="raw-json-container">
        <div className="panel-header">
          <h2><Code size={16} /> Raw JSON</h2>
        </div>
        <div className="empty-state">
          <FileJson size={32} className="empty-icon" />
          <p className="no-data">No metadata available</p>
        </div>
      </div>
    );
  }

  // Filter based on search term (applied to pure metadata)
  let filteredMetadata = pureMetadata;
  if (searchTerm) {
    try {
      filteredMetadata = JSON.parse(
        JSON.stringify(pureMetadata, (key, value) =>
          String(key).toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
            ? value
            : undefined
        )
      );
    } catch (error) {
      console.error('Error filtering metadata:', error);
      filteredMetadata = pureMetadata;
    }
  }

  return (
    <div className="raw-json-container">
      <div className="panel-header">
        <h2><Code size={16} /> Raw JSON</h2>
        <button className="copy-btn" onClick={handleCopy}>
          {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
        </button>
      </div>
      {filteredMetadata ? (
        <pre
          className="raw-json"
          dangerouslySetInnerHTML={{ __html: syntaxHighlight(filteredMetadata) }}
        />
      ) : (
        <p className="no-data">No metadata available</p>
      )}
    </div>
  );
}

export default RawJSONView;